import React from "react";
import ReactGA from "react-ga";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
  Grid,
  TextField,
  Checkbox,
  Button,
  Paper,
  Typography,
  FormHelperText,
  FormLabel,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import CloseIcon from "@material-ui/icons/Close";
import firebase from "firebase/app";
import "firebase/functions";
import "firebase/firestore";
import { v4 as uuidv4 } from "uuid";

import LocationAutoComplete from "../Components/LocationAutoComplete";
import StarWidget from "../Components/StarWidget";

const initialState = {
  submitting: false,
  submitted: false,
  companyName: "",
  reviewTitle: "",
  nomad: false,
  hoursOfWork: "",
  overlap: "",
  socialize: "",
  meet: "",
  impact: null,
  promotion: "",
  remoteTestimony: "",
  department: "",
  location: null,
  rating: 0,
  errors: {
    location: null,
    hoursOfWork: null,
    overlap: null,
    socialize: null,
    meet: null,
    impact: null,
    promotion: null,
  },
};

class ReviewForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = initialState;
  }

  updateRating = (newRating) => this.setState({ rating: newRating });

  handleRadioButton = (e) => {
    // Adjust error state so that textfields can reflect issues
    switch (e.currentTarget.id) {
      case "nomad":
        this.setState({
          ...this.state,
          nomad: !this.state.nomad,
        });
        break;
      case "hoursOfWork":
        this.setState({
          ...this.state,
          hoursOfWork: e.currentTarget.value,
        });
        break;
      case "overlap":
        this.setState({
          ...this.state,
          overlap: e.currentTarget.value,
        });
        break;
      case "socialize":
        this.setState({
          ...this.state,
          socialize: e.currentTarget.value,
        });
        break;
      case "meet":
        this.setState({
          ...this.state,
          meet: e.currentTarget.value,
        });
        break;
      case "impact":
        this.setState({
          ...this.state,
          impact: e.currentTarget.value === "true" ? true : false,
        });
        break;
      case "promotion":
        this.setState({
          ...this.state,
          promotion: e.currentTarget.value,
        });
        break;
      default:
        break;
    }
  };

  handleTextChange = (e) => {
    this.setState({
      ...this.state,
      [e.currentTarget.id]: e.currentTarget.value,
    });
  };

  submissionErrorCheck() {
    let newErrors = {},
      errorFound = false;
    // Set general errors
    newErrors.location = this.state.location === null ? true : false;
    newErrors.reviewTitle = this.state.reviewTitle === "" ? true : false;
    newErrors.department = this.state.department === "" ? true : false;
    newErrors.rating = this.state.rating === 0 ? true : false;

    // Check if error was found
    Object.keys(newErrors).forEach((key) => {
      if (newErrors[key] === true) {
        errorFound = true;
      }
    });
    // Set state with object
    this.setState({
      ...this.state,
      errors: newErrors,
    });
    return errorFound;
  }

  handleDepartmentSelection = (e) =>
    this.setState({ department: e.target.value });

  handleSubmit = (e) => {
    e.preventDefault();
    if (this.submissionErrorCheck()) {
      return;
    }

    ReactGA.event({
      category: "Data Submission",
      action: "Submitted Remote Review",
      label: "ReviewForm.js",
    });

    // Firebase
    const db = firebase.firestore();

    let companyName = this.props.companyName ? this.props.companyName : this.state.companyName;
    const friendlyCompanyName = companyName.replace(
      /[^a-zA-Z0-9-_]/g,
      ""
    );
    const submissionId = `${friendlyCompanyName}-${uuidv4()}`;
    const companyRef = db.collection("reviews").doc(submissionId);
    this.setState(
      { submitting: true, error: false, errorTxt: "" },
      async () => {
        await companyRef.set({
          companyName: companyName,
          nomad: this.state.nomad,
          location: this.state.location,
          remoteTestimony: this.state.remoteTestimony,
          department: this.state.department,
          rating: this.state.rating,
          reviewTitle: this.state.reviewTitle,
          submissionDate: new Date(),
          verified: false,
          version: 2,
          deleted: false
        });
        // Send verification email
        const sendVerificationEmail = firebase
          .functions()
          .httpsCallable("sendVerificationEmail");
        sendVerificationEmail({
          type: "remoteReview",
          companyName: companyName,
          nomad: this.state.nomad,
          submissionId,
          location: this.state.location,
          remoteTestimony: this.state.remoteTestimony,
          department: this.state.department,
          rating: this.state.rating,
          reviewTitle: this.state.reviewTitle,
          submissionDate: new Date(),
          verified: false,
        });
        // Set state

        if (this.state.email) {
          await db.collection("airpodSubmissions").add({
            id: uuidv4(),
            email: this.state.email.toLowerCase()
          });
        }

        this.setState({ submitting: false, submitted: true });

        if (this.props.submitComplete) {
          this.props.submitComplete();
        }
      }
    );
  };

  clearForm = () => {
    this.setState({ ...initialState });
  };

  setLocation = (value) => this.setState({ location: value });

  getMenuItems = () => {
    const departments = [
      "Accounting",
      "Design",
      "Engineering",
      "Finance",
      "HR",
      "Marketing",
      "Operations",
      "Product",
      "Recruiting",
      "Sales",
      "Other",
    ];

    return departments.map((item) => {
      return <MenuItem value={item}>{item}</MenuItem>;
    });
  };

  getCompanyList = () => {
    return this.props.companyList.map((item) => {
      return <MenuItem value={item}>{item}</MenuItem>;
    })
  }

  handleCompanySelection = (e) => 
    this.setState({ companyName: e.target.value })

  render() {
    const { classes, isMobile } = this.props;
    const { submitting, submitted, department, location } = this.state;

    if (submitted) {
      return (
        <div className={classes.root} id="inputForm">
          <Paper
            className={classes.paper}
            variant="outlined"
            style={{ width: "100%" }}
          >
            <form onSubmit={(e) => e.preventDefault()} style={{ flexGrow: 1 }}>
              <Grid container spacing={1} className={classes.formBoxContent}>
                <Grid item xs={12}>
                  {isMobile ? (
                    <Typography variant="h6" align="center" gutterBottom>
                      <b>Successfully submitted, thanks!</b>
                    </Typography>
                  ) : (
                    <Typography variant="h6" align="center" gutterBottom>
                      <b>Successfully submitted, thanks!</b>
                    </Typography>                    
                  )}
                </Grid>
              </Grid>
            </form>
          </Paper>
        </div>
      );
    }

    const { companyName = this.state.companyName, containerStyle = {}, toggleForm, companyList } = this.props;

    let mobileStyle = isMobile ? { textAlign: "center" } : null;

    let txtAreaLabel = isMobile
      ? ""
      : `Describe Your Remote Experience at ${companyName} (optional)`;

    return (
      <div className={classes.root} id="inputForm">
        <Paper className={classes.paper} variant="outlined">
          <form onSubmit={this.handleSubmit}>
            <Grid container justify="flex-end">
              {isMobile && (
                <CloseIcon
                  style={{ marginTop: "15px", marginRight: "15px" }}
                  onClick={toggleForm}
                />
              )}
            </Grid>
            <Grid
              container
              className={classes.formBoxContent}
              style={containerStyle}
            >
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  style={{ textAlign: "center", ...mobileStyle }}
                >
                  <b>Tell Us About Your Remote Experience at {companyName}</b>
                </Typography>
              </Grid>
              { 
                !this.props.companyName ? (
                  <Grid item xs={12}>
                    <FormControl
                      xs={12}
                      className={classes.formControl}
                      style={{ marginLeft: "0", width: "100%", marginTop: "25px" }}
                    >
                      <InputLabel id="demo-simple-select-label">
                        Select Your Company
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="companyName"
                        value={this.state.companyName}
                        onChange={this.handleCompanySelection}
                        style={{ width: "100%" }}
                      >
                        {this.getCompanyList()}
                      </Select>
                      <FormHelperText error={this.state.errors.companyName}>
                        {this.state.errors.companyName
                          ? "This field is required"
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                ) : null 
              }
              <Grid item xs={12}>
                <TextField
                  id="reviewTitle"
                  label="Name Your Review"
                  onChange={this.handleTextChange}
                  style={{ width: "100%" }}
                />
                <FormHelperText error={this.state.errors.reviewTitle}>
                  {this.state.errors.reviewTitle
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  xs={12}
                  className={classes.formControl}
                  style={{ marginLeft: "0", width: "100%", marginTop: "25px" }}
                >
                  <InputLabel id="demo-simple-select-label">
                    Select Your Department
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="department"
                    value={department}
                    onChange={this.handleDepartmentSelection}
                    style={{ width: "100%" }}
                  >
                    {this.getMenuItems()}
                  </Select>
                  <FormHelperText error={this.state.errors.department}>
                    {this.state.errors.department
                      ? "This field is required"
                      : ""}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <LocationAutoComplete setLocation={this.setLocation} />
                <FormHelperText error={this.state.errors.location}>
                  {this.state.errors.location ? "This field is required" : ""}
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormGroup component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="nomad"
                        size="small"
                        color="primary"
                        checked={this.state.nomad}
                        onChange={this.handleRadioButton}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        align="left"
                        style={{ fontWeight: "bold" }}
                        className={classes.digitalNomad}
                      >
                        Currently Traveling // Digital Nomad
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                </FormGroup>
              </Grid>
              <Grid
                item
                xs={12}
                align="center"
                className={classes.addMarginTop}
              >
                <TextField
                  id="remoteTestimony"
                  label={txtAreaLabel}
                  multiline
                  rows={4}
                  defaultValue=""
                  variant="outlined"
                  style={{ width: "100%", marginBottom: "35px" }}
                  onChange={this.handleTextChange}
                />
                <FormControl>
                  <FormLabel
                    style={{ color: "black", marginBottom: "6px" }}
                    component="legend"
                  >
                    Rate your remote experience at {companyName}
                  </FormLabel>
                  <StarWidget updateRating={this.updateRating} />
                  <FormHelperText error={this.state.errors.rating}>
                    {this.state.errors.rating ? "This field is required" : ""}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <h4 style={{ marginTop: "34px", textAlign: "left" }}>
                  {" "}
                  Airpods Giveaway (optional){" "}
                </h4> 
                <div style={{ textAlign: "left", fontSize: "16px" }}>
                  Enter your email for a chance to win a pair of Apple Airpods. 1 in 100 submissions is a winner.
                </div>   
                <Grid
                  item
                  xs={12}
                  align="center"
                  className={classes.addMarginTop}
                >                
                <React.Fragment>
                    <div
                      id="source-section"
                      style={{
                        transition: "all 2s",
                      }}
                    >
                      <Grid item xs={12} style={{ marginBottom: "15px" }}>
                        <TextField
                          id="email"
                          label="Email"
                          fullWidth
                          placeholder="your@email.com"
                          onChange={this.handleTextChange}
                        />
                      </Grid>
                    </div>
                  </React.Fragment>   
                </Grid>
              <Grid
                item
                xs={12}
                style={{ textAlign: "right", marginTop: "40px" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  size="large"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    );
  }
}

const styles = (theme) => ({
  root: {
    display: "flex",
  },
  formControl: {
    margin: theme.spacing(3),
  },
  addMarginTop: {
    marginTop: "15px",
  },
  paper: {
    border: "none",
  },
  formBoxContent: {
    padding: theme.spacing(4),
    paddingTop: 0,
  },
  "@media (max-device-width: 767px)": {
    formBoxContent: {
      padding: 0
    },
    digitalNomad: {
      fontSize: "14px",
    },
  },
});

export default withStyles(styles)(ReviewForm);
